import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaSpinner, FaTrash } from 'react-icons/fa';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import Header from './Header';
import './Home.css';

interface Project {
  id: string;
  name: string;
  created_at: string;
}

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingProject, setDeletingProject] = useState<string | null>(null);

  const fetchProjects = useCallback(async () => {
    if (!user) {
      console.log('No user found when trying to fetch projects');
      setError('User not authenticated');
      setLoading(false);
      return;
    }

    try {
      console.log('Fetching projects for user:', user.id);
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('Projects fetched:', data);
      setProjects(data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError(error instanceof Error ? error.message : 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    console.log('Auth state:', { user, session });
    if (session && user) {
      console.log('User authenticated, fetching projects for user:', user.id);
      fetchProjects();
    } else {
      console.log('No authenticated user found');
      setLoading(false);
    }
  }, [session, fetchProjects, user]);

  const handleCreateProject = async () => {
    if (!user) {
      setError('Please sign in to create a project');
      return;
    }

    try {
      console.log('Creating project for user:', user.id);
      const defaultName = projects.length === 0 
        ? "Untitled Project" 
        : `New Project ${projects.length + 1}`;

      const { data, error } = await supabase
        .from('projects')
        .insert([{ 
          name: defaultName,
          user_id: user.id
        }])
        .select()
        .single();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      if (!data) {
        throw new Error('No data returned from insert');
      }

      console.log('Project created:', data);
      navigate(`/project/${data.id}`);
    } catch (error) {
      console.error('Error creating project:', error);
      setError(error instanceof Error ? error.message : 'Failed to create project');
    }
  };

  const handleDeleteProject = async (projectId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!user) return;

    try {
      setDeletingProject(projectId);
      
      // Delete all artifacts associated with the project
      const { error: artifactsError } = await supabase
        .from('artifacts')
        .delete()
        .eq('project_id', projectId);

      if (artifactsError) throw artifactsError;

      // Delete all ideas associated with the project
      const { error: ideasError } = await supabase
        .from('ideas')
        .delete()
        .eq('project_id', projectId);

      if (ideasError) throw ideasError;

      // Delete the project itself
      const { error: projectError } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);

      if (projectError) throw projectError;

      // Update local state
      setProjects(prev => prev.filter(p => p.id !== projectId));
    } catch (error) {
      console.error('Error deleting project:', error);
      setError(error instanceof Error ? error.message : 'Failed to delete project');
    } finally {
      setDeletingProject(null);
    }
  };

  if (loading) {
    return (
      <div className="home-container">
        <Header />
        <div className="loading">
          <FaSpinner className="spinner" />
          <p>Loading projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home-container">
      <Header />
      <div className="content">
        <div className="projects-grid">
          <div className="project-card new-project" onClick={handleCreateProject}>
            <FaPlus className="plus-icon" />
            <h3>Create New Project</h3>
          </div>
          {projects.map((project) => (
            <div
              key={project.id}
              className="project-card"
              onClick={() => navigate(`/project/${project.id}`)}
            >
              <div className="project-header">
                <h3>{project.name}</h3>
                <button 
                  className="delete-button"
                  onClick={(e) => handleDeleteProject(project.id, e)}
                  disabled={deletingProject === project.id}
                >
                  {deletingProject === project.id ? (
                    <FaSpinner className="spinner" />
                  ) : (
                    <FaTrash />
                  )}
                </button>
              </div>
              <p className="project-date">
                Created {new Date(project.created_at).toLocaleDateString()}
              </p>
            </div>
          ))}
        </div>
        {error && <p className="error-message">{error}</p>}
        {!error && projects.length === 0 && (
          <p className="no-projects">No projects yet. Create your first project!</p>
        )}
      </div>
    </div>
  );
};

export default Home;